import {
  useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type { ItemHomeBody, SharingDTO, SharingEffect, SharingModeBody } from '@contracts/sharing';
import type { ItemRecord } from '@contracts/workplace';
import { api, type ApiClientError } from '../../api/client';
import { PROJECTS_KEY, projectKey } from '../projects/api';
import { TREE_KEY } from '../workplace/cache';
import { CHANGES_KEY } from '../workplace/changes';
import { PALETTE_CARDS_KEY } from '../workplace/palette-cards';

/**
 * A home's sharing on the web (sharing spec §8.1): read with `GET …/sharing`, changed by mode, by adding people and by
 * removing one. Every write carries an idempotency key; a success re-reads what the audience decides (the board, the
 * project lists) and an error re-reads the sharing itself, so what is on screen is what the server holds (L5-3).
 */

export type HomeRef = { kind: 'item' | 'project'; id: Id };

export const sharingKey = (h: HomeRef) => ['sharing', h.kind, h.id] as const;

const base = (h: HomeRef): string => (h.kind === 'item'
  ? `/api/workplace/items/${encodeURIComponent(h.id)}/sharing`
  : `/api/projects/${encodeURIComponent(h.id)}/sharing`);

/** `enabled` false: the home was just left, and is not to be read again. */
export function useSharing(h: HomeRef, enabled = true): UseQueryResult<SharingDTO, ApiClientError> {
  return useQuery<SharingDTO, ApiClientError>({
    queryKey: sharingKey(h), queryFn: () => api<SharingDTO>(base(h)), retry: false, enabled,
  });
}

/** What the audience decides beyond the sharing itself: the board, the done list, the trees, the projects. */
function settled(queryClient: QueryClient, h: HomeRef, answer: SharingDTO | null) {
  // Left and no longer seeing it: the caller forgets the home (`forgetHome`), there is nothing to re-read.
  if (answer === null) return;
  queryClient.setQueryData(sharingKey(h), answer);
  void queryClient.invalidateQueries({ queryKey: sharingKey(h) });
  void queryClient.invalidateQueries({ queryKey: ['workplace'] });
  void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  if (h.kind === 'project') void queryClient.invalidateQueries({ queryKey: projectKey(h.id) });
}

function useSharingWrite<T extends SharingDTO | null, V>(h: HomeRef, send: (vars: V, requestId: Id) => Promise<T>) {
  const queryClient = useQueryClient();
  return useMutation<T, ApiClientError, V>({
    mutationFn: (vars) => send(vars, crypto.randomUUID()),
    onSuccess: (answer) => settled(queryClient, h, answer),
    // A refused write re-reads the sharing: a conflict's next click then sends the version the server holds.
    onError: () => { void queryClient.invalidateQueries({ queryKey: sharingKey(h) }); },
  });
}

export function useChangeMode(h: HomeRef): UseMutationResult<SharingDTO, ApiClientError, SharingModeBody> {
  return useSharingWrite(h, (body: SharingModeBody, requestId) =>
    api<SharingDTO>(base(h), { method: 'PATCH', body, requestId }));
}

export function useAddMembers(h: HomeRef): UseMutationResult<SharingDTO & SharingEffect, ApiClientError, Id[]> {
  return useSharingWrite(h, (userIds: Id[], requestId) =>
    api<SharingDTO & SharingEffect>(`${base(h)}/members`, { method: 'POST', body: { userIds }, requestId }));
}

/** `null` is the 204 of leaving a home one no longer sees. The empty body is load-bearing: the client always sends
 * `Content-Type: application/json`, and the API answers 422 to a JSON-typed request with no body. */
export function useRemoveMember(h: HomeRef): UseMutationResult<SharingDTO | null, ApiClientError, Id> {
  return useSharingWrite(h, async (userId: Id, requestId) =>
    (await api<SharingDTO | undefined>(`${base(h)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE', body: {}, requestId })) ?? null);
}

/**
 * "Đổi nhà" (sharing spec §8.4): the branch under `itemId` moves to a project, under a parent, or to a home of its own.
 * Everything the audience decides is read again: the board, the sharing of every home, the card, the projects.
 */
export function useChangeHome(itemId: Id): UseMutationResult<ItemRecord & SharingEffect, ApiClientError, ItemHomeBody> {
  const queryClient = useQueryClient();
  return useMutation<ItemRecord & SharingEffect, ApiClientError, ItemHomeBody>({
    mutationFn: (body) => api<ItemRecord & SharingEffect>(
      `/api/workplace/items/${encodeURIComponent(itemId)}/home`, { method: 'POST', body, requestId: crypto.randomUUID() },
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sharing'] });
      void queryClient.invalidateQueries({ queryKey: ['workplace'] });
      void queryClient.invalidateQueries({ queryKey: ['task', itemId] });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/** The reads that mix cards of many homes: after losing sight of one, they are read again from nothing. */
const MIXED_KEYS = [
  TREE_KEY, CHANGES_KEY, PALETTE_CARDS_KEY, ['history'], ['activity'], ['dashboard'], ['notifications'], ['strategy'],
] as const;

/** After leaving a home one no longer sees (L5-2): nothing of it stays in the cache. */
export function forgetHome(queryClient: QueryClient, h: HomeRef) {
  queryClient.removeQueries({ queryKey: sharingKey(h) });
  if (h.kind === 'item') queryClient.removeQueries({ queryKey: ['task', h.id] });
  else queryClient.removeQueries({ queryKey: projectKey(h.id) });
  void queryClient.invalidateQueries({ queryKey: ['workplace'] });
  void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  for (const key of MIXED_KEYS) void queryClient.resetQueries({ queryKey: key });
}
